import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useDispatch } from "react-redux";
import { retryMatterErrors } from "../../service/matter";
import { fetchMatterDetail } from "../../slice/MatterDetailsSlice";
import MatterTierBadge from "./MatterTierBadge";
import {
  ErrorList,
  ErrorNotice,
  ErrorPanel,
  ErrorPanelHeader,
  ErrorRow,
  ErrorRowMeta,
  ErrorRowName,
  Pill,
  PillCount,
  PillLabel,
  PrimaryButton,
  ReadyBadge,
  SecondaryButton,
  StatusBar,
  StatusLeft,
  StatusRight,
} from "./MatterProcessingStatusStyled";

/**
 * Per-matter AI pipeline status surface.
 *
 * Renders just above the documents table on the matter detail page. Reads
 * the rollups + errored-documents list that `dms_backend` PRs #655 / #656
 * expose on the matter detail payload:
 *
 *   - `documents_total`             total count of (non-soft-deleted) docs
 *   - `documents_processing_count`  docs in queued/extracting/categorizing/graphing/embedding
 *   - `documents_review_count`      docs in `pipeline_status='review'` (LLM stalled)
 *   - `documents_error_count`       docs in `pipeline_status='failed'`
 *   - `documents_legacy_count`      docs with NO AI-pipeline involvement (legacy
 *                                   DocRio catalog rows synced from Salesforce;
 *                                   null on list payloads — detail-only)
 *   - `matter_tier`                 Matthew's three-tier matter classification
 *                                   ("ai" / "legacy_sorted" / "legacy_unsorted";
 *                                   null on list payloads — detail-only). Renders
 *                                   as a small `MatterTierBadge` pill at the left
 *                                   edge of the status bar; null renders nothing
 *                                   so the bar is byte-identical to pre-badge
 *                                   payloads.
 *   - `errored_documents`           ranked list (id, name, pipeline_status, ...)
 *
 * Legacy scope (2026-07-10): since the re-plug the live DB carries legacy
 * DocRio catalog rows that never touched the AI pipeline. They count into
 * `documents_total` but into none of the processing/review/error buckets,
 * so the derived "processed = total - processing - review - error" math
 * claimed they were all processed ("All 2,669 documents processed" on a
 * matter with 341 AI-processed docs). When the backend ships a positive
 * `documents_legacy_count` we subtract it and render honest copy; when
 * it's null/0 (pure-AI matters, list payloads, old backends) the copy is
 * byte-identical to what it was before.
 *
 * The component never blocks the rest of the page — when the matter has
 * zero errors AND zero in-flight work it shows a single "Ready for
 * analysis" badge instead of the breakdown so the chrome stays quiet on
 * the happy path. When there's anything in flight or any failure it
 * renders the full breakdown plus a per-doc / retry-all action surface.
 *
 * Retries call `POST /api/matters/<id>/documents/retry-errors/` which
 * resubmits the latest `ExternalPipelineJob` for each failed doc and
 * returns `{ resubmitted, submit_failed, skipped, truncated,
 * total_candidate_count, orphan_documents, ... }`. After a successful
 * retry we re-fire `fetchMatterDetail` so the rollups (which are
 * computed server-side) refresh in place. If the backend reports
 * truncation we surface the "X of Y retried — click again to drain the
 * rest" message; if any orphans were returned we surface them as
 * "needs a fresh upload" because the matter has docs in `failed` with
 * no prior pipeline job to resubmit.
 */

export const PROCESSING_POLL_INTERVAL_MS = 20_000;
export const IDLE_POLL_INTERVAL_MS = 60_000;

const PROCESSING_FRIENDLY_LABELS = {
  queued: "Queued",
  extracting: "Extracting",
  categorizing: "Categorizing",
  graphing: "Building graph",
  embedding: "Embedding",
  review: "Needs Review",
  failed: "Failed",
};

function formatStatus(status) {
  if (!status) return "";
  const lower = String(status).toLowerCase();
  return PROCESSING_FRIENDLY_LABELS[lower] || lower;
}

const MatterProcessingStatus = ({ matter }) => {
  const dispatch = useDispatch();
  const [retryingAll, setRetryingAll] = useState(false);
  const [retryingDocIds, setRetryingDocIds] = useState(new Set());
  const [expanded, setExpanded] = useState(false);
  const [lastRetrySummary, setLastRetrySummary] = useState(null);

  const matterId = matter?.id;

  const counts = useMemo(() => {
    const total = Number.isFinite(matter?.documents_total)
      ? matter.documents_total
      : null;
    const processing = Number.isFinite(matter?.documents_processing_count)
      ? matter.documents_processing_count
      : 0;
    const review = Number.isFinite(matter?.documents_review_count)
      ? matter.documents_review_count
      : 0;
    const error = Number.isFinite(matter?.documents_error_count)
      ? matter.documents_error_count
      : 0;
    // null/undefined (list payloads, old backends) coerces to 0 — the
    // legacy-aware copy only kicks in on a positive count, so pure-AI
    // matters render exactly what they rendered before this field existed.
    const legacy = Number.isFinite(matter?.documents_legacy_count)
      ? matter.documents_legacy_count
      : 0;
    // Documents actually in AI scope. Clamped so a payload where
    // legacy > total (shouldn't happen) can't render negative counts.
    const aiTotal = total === null ? null : Math.max(total - legacy, 0);
    return { total, processing, review, error, legacy, aiTotal };
  }, [matter]);

  const errored = useMemo(
    () => (Array.isArray(matter?.errored_documents) ? matter.errored_documents : []),
    [matter]
  );

  const hasAnyActivity =
    counts.total !== null &&
    (counts.processing > 0 || counts.review > 0 || counts.error > 0);

  // "Ready for analysis" means every AI-SCOPE document is processed — the
  // green pill must not fire on the back of legacy records that were never
  // AI-processed at all.
  const isReady =
    counts.total !== null &&
    counts.total > 0 &&
    counts.aiTotal > 0 &&
    counts.processing === 0 &&
    counts.review === 0 &&
    counts.error === 0;

  // Matter is ALL legacy records: nothing has entered the AI pipeline yet,
  // so there's nothing to be "ready" about and nothing in flight.
  const isAllLegacy =
    counts.total !== null &&
    counts.total > 0 &&
    counts.aiTotal === 0 &&
    counts.legacy > 0 &&
    !hasAnyActivity;

  const refreshMatter = useCallback(() => {
    if (matterId) dispatch(fetchMatterDetail(matterId));
  }, [dispatch, matterId]);

  // Rollups are computed server-side and only arrive on a matter-detail
  // fetch — there is no websocket for pipeline status. Poll quickly while
  // work is active and slowly while the latest snapshot is terminal. The
  // slow poll is necessary when a retry starts elsewhere (Processing page,
  // admin operation, automatic recovery): otherwise a banner that once saw
  // processing=0 never learns that work restarted.
  useEffect(() => {
    if (!matterId || counts.total === null || counts.total <= 0) return undefined;
    const interval =
      counts.processing > 0 ? PROCESSING_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS;
    const timer = setInterval(() => {
      dispatch(fetchMatterDetail(matterId));
    }, interval);
    return () => clearInterval(timer);
  }, [dispatch, matterId, counts.processing, counts.total]);

  const handleRetryAll = useCallback(async () => {
    if (!matterId || retryingAll) return;
    setRetryingAll(true);
    try {
      const response = await retryMatterErrors(matterId);
      const summary = response?.data || {};
      setLastRetrySummary(summary);
      const resubmitted = summary.resubmitted || 0;
      const submitFailed = summary.submit_failed || 0;
      const truncated = Boolean(summary.truncated);
      const totalCandidates = summary.total_candidate_count || 0;
      if (resubmitted > 0 && truncated) {
        message.success(
          `Retrying ${resubmitted} of ${totalCandidates} failed documents. Click "Retry all" again to drain the rest.`
        );
      } else if (resubmitted > 0) {
        message.success(`Retrying ${resubmitted} failed document${resubmitted === 1 ? "" : "s"}.`);
      } else if (submitFailed > 0) {
        message.error("Couldn't reach the AI pipeline. Try again in a minute.");
      } else {
        message.info("Nothing to retry on this matter.");
      }
      refreshMatter();
    } catch (err) {
      message.error("Retry failed. Please try again.");
    } finally {
      setRetryingAll(false);
    }
  }, [matterId, retryingAll, refreshMatter]);

  const handleRetryOne = useCallback(
    async (docId) => {
      if (!matterId || !docId || retryingDocIds.has(docId)) return;
      setRetryingDocIds((prev) => {
        const next = new Set(prev);
        next.add(docId);
        return next;
      });
      try {
        const response = await retryMatterErrors(matterId, {
          documentIds: [docId],
        });
        const summary = response?.data || {};
        const orphan = (summary.orphan_documents || []).find(
          (o) => o.document_id === docId
        );
        if (orphan) {
          message.warning(
            "This document never reached the AI pipeline — please re-upload it."
          );
        } else if ((summary.resubmitted || 0) > 0) {
          message.success("Retrying document.");
        } else if ((summary.submit_failed || 0) > 0) {
          message.error("Couldn't reach the AI pipeline. Try again in a minute.");
        } else {
          message.info("Nothing to retry for this document.");
        }
        refreshMatter();
      } catch (err) {
        message.error("Retry failed. Please try again.");
      } finally {
        setRetryingDocIds((prev) => {
          const next = new Set(prev);
          next.delete(docId);
          return next;
        });
      }
    },
    [matterId, retryingDocIds, refreshMatter]
  );

  // Don't render at all until we actually have rollup data on the matter
  // payload — otherwise we'd flash a "0 documents" pill while the
  // first fetch is in flight.
  if (!matter || counts.total === null) return null;

  if (counts.total === 0) {
    return (
      <StatusBar role="region" aria-label="Matter processing status">
        <StatusLeft>
          <PillLabel style={{ fontSize: 12 }}>No documents yet — upload to start AI processing.</PillLabel>
        </StatusLeft>
      </StatusBar>
    );
  }

  if (isAllLegacy) {
    // Every document on the matter is a legacy record from the old DocRio
    // system — none of them have been AI-processed, so the green badge
    // would be a lie. Quiet informational bar instead. The tier badge
    // (sorted vs unsorted claim file) is most useful exactly here.
    return (
      <StatusBar role="region" aria-label="Matter processing status">
        <StatusLeft>
          <MatterTierBadge tier={matter?.matter_tier} />
          <PillLabel style={{ fontSize: 12 }}>
            {counts.legacy.toLocaleString()} legacy record{counts.legacy === 1 ? "" : "s"} not yet AI-processed — upload to start AI processing.
          </PillLabel>
        </StatusLeft>
      </StatusBar>
    );
  }

  if (isReady) {
    return (
      <StatusBar role="region" aria-label="Matter processing status">
        <StatusLeft>
          <MatterTierBadge tier={matter?.matter_tier} />
          <ReadyBadge>Ready for analysis</ReadyBadge>
          {counts.legacy > 0 ? (
            <PillLabel style={{ fontSize: 12, color: "#48566B" }}>
              {counts.aiTotal.toLocaleString()} document{counts.aiTotal === 1 ? "" : "s"} AI-processed · {counts.legacy.toLocaleString()} legacy record{counts.legacy === 1 ? "" : "s"} not yet AI-processed.
            </PillLabel>
          ) : (
            <PillLabel style={{ fontSize: 12, color: "#48566B" }}>
              All {counts.total} document{counts.total === 1 ? "" : "s"} processed.
            </PillLabel>
          )}
        </StatusLeft>
      </StatusBar>
    );
  }

  const showErrorPanel = expanded && counts.error > 0;
  // Backend caps `errored_documents` at 25, but `documents_error_count`
  // is the true total — surface the gap so the user knows the list is
  // a preview.
  const previewLimit = errored.length;
  const moreErroredCount = Math.max(counts.error - previewLimit, 0);

  return (
    <div role="region" aria-label="Matter processing status">
      <StatusBar>
        <StatusLeft>
          <MatterTierBadge tier={matter?.matter_tier} />
          {counts.processing > 0 && (
            <Pill $variant="processing" $interactive={false} aria-label={`${counts.processing} documents processing`}>
              <PillCount>{counts.processing}</PillCount>
              <PillLabel>Processing</PillLabel>
            </Pill>
          )}
          {counts.review > 0 && (
            <Pill $variant="review" $interactive={false} aria-label={`${counts.review} documents need review`}>
              <PillCount>{counts.review}</PillCount>
              <PillLabel>Needs Review</PillLabel>
            </Pill>
          )}
          {counts.error > 0 && (
            <Pill
              $variant="failed"
              $interactive
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="matter-error-panel"
            >
              <PillCount>{counts.error}</PillCount>
              <PillLabel>Failed</PillLabel>
              <span aria-hidden="true">{expanded ? "▴" : "▾"}</span>
            </Pill>
          )}
          {!hasAnyActivity && (
            <Pill $variant="neutral" $interactive={false}>
              <PillLabel>No active processing</PillLabel>
            </Pill>
          )}
          {counts.total > 0 && counts.legacy > 0 ? (
            <PillLabel style={{ fontSize: 12, color: "#48566B" }}>
              of {counts.aiTotal.toLocaleString()} AI document{counts.aiTotal === 1 ? "" : "s"} · {counts.legacy.toLocaleString()} legacy record{counts.legacy === 1 ? "" : "s"} not yet AI-processed
            </PillLabel>
          ) : counts.total > 0 ? (
            <PillLabel style={{ fontSize: 12, color: "#48566B" }}>
              of {counts.total} document{counts.total === 1 ? "" : "s"}
            </PillLabel>
          ) : null}
        </StatusLeft>

        <StatusRight>
          {counts.error > 0 && (
            <PrimaryButton
              type="button"
              onClick={handleRetryAll}
              disabled={retryingAll}
              aria-label="Retry all failed documents"
            >
              {retryingAll ? "Retrying…" : `Retry all (${counts.error})`}
            </PrimaryButton>
          )}
        </StatusRight>
      </StatusBar>

      {showErrorPanel && (
        <ErrorPanel id="matter-error-panel">
          <ErrorPanelHeader>
            <span>{counts.error} document{counts.error === 1 ? "" : "s"} failed</span>
            <SecondaryButton
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Hide error list"
            >
              Hide
            </SecondaryButton>
          </ErrorPanelHeader>
          {errored.length === 0 ? (
            <ErrorNotice>
              Failure details are still loading. Use "Retry all" to retry every failed document on this matter.
            </ErrorNotice>
          ) : (
            <ErrorList>
              {errored.map((doc) => {
                const isRetrying = retryingDocIds.has(doc.id);
                return (
                  <ErrorRow key={doc.id}>
                    <ErrorRowName title={doc.name || `Document ${doc.id}`}>
                      {doc.name || `Document ${doc.id}`}
                    </ErrorRowName>
                    <ErrorRowMeta>{formatStatus(doc.pipeline_status) || "Failed"}</ErrorRowMeta>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleRetryOne(doc.id)}
                      disabled={isRetrying}
                      aria-label={`Retry ${doc.name || `Document ${doc.id}`}`}
                    >
                      {isRetrying ? "Retrying…" : "Retry"}
                    </SecondaryButton>
                  </ErrorRow>
                );
              })}
            </ErrorList>
          )}
          {moreErroredCount > 0 && (
            <ErrorNotice>
              Showing the {previewLimit} most-recent failures. Use "Retry all" above to also retry the remaining {moreErroredCount}.
            </ErrorNotice>
          )}
          {lastRetrySummary?.truncated && (
            <ErrorNotice>
              Last retry started {lastRetrySummary.resubmitted || 0} of {lastRetrySummary.total_candidate_count || 0} failed documents. Click "Retry all" again to start the rest.
            </ErrorNotice>
          )}
          {lastRetrySummary?.orphan_documents?.length > 0 && (
            <ErrorNotice>
              {lastRetrySummary.orphan_documents.length} document
              {lastRetrySummary.orphan_documents.length === 1 ? "" : "s"} never reached the AI pipeline and need to be re-uploaded.
            </ErrorNotice>
          )}
        </ErrorPanel>
      )}
    </div>
  );
};

MatterProcessingStatus.propTypes = {
  matter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    documents_total: PropTypes.number,
    documents_processing_count: PropTypes.number,
    documents_review_count: PropTypes.number,
    documents_error_count: PropTypes.number,
    documents_legacy_count: PropTypes.number,
    matter_tier: PropTypes.oneOf(["ai", "legacy_sorted", "legacy_unsorted", null]),
    errored_documents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string,
        pipeline_status: PropTypes.string,
      })
    ),
  }),
};

export default MatterProcessingStatus;
